import { useCallback, useEffect, useRef, useState } from "react";
import {
  LuArrowDownToLine,
  LuArrowUpFromLine,
  LuLock,
  LuChevronRight,
  LuWallet,
} from "react-icons/lu";
import { TpvDialog } from "../../components/TpvDialog";
import {
  getCashState,
  getCashHistory,
  getCashClose,
  submitCashOperation,
} from "../../api/cash";
import "./style.css";
const euros = (cents) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(
    Number(cents || 0) / 100,
  );
const date = (value) =>
  value
    ? new Intl.DateTimeFormat("es-ES", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value))
    : "—";
const reasons = {
  IN: [
    ["additional_float", "Fondo adicional"],
    ["exchange", "Cambio de moneda"],
    ["pending_collection", "Cobro pendiente"],
    ["other", "Otro"],
  ],
  OUT: [
    ["supplier", "Pago a proveedor"],
    ["withdrawal", "Retirada de efectivo"],
    ["refund", "Devolución"],
    ["exchange", "Cambio de moneda"],
    ["other", "Otro"],
  ],
};
export const readCashAmount = (value) =>
  /^\d{1,7}([,.]\d{0,2})?$/.test(String(value))
    ? Number(String(value).replace(",", "."))
    : NaN;
export const cashJournalKey = (data) =>
  `delibran.cash.pending.${data.pointOfSaleId}.${data.operatorId}`;
const readJournal = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "null");
    return value &&
      typeof value.body?.requestId === "string" &&
      (value.path === "cash-register/open" ||
        /^cash-register\/shifts\/[0-9a-f-]{36}\/(close|movements)$/.test(
          value.path,
        ))
      ? value
      : null;
  } catch {
    return null;
  }
};
const writeJournal = (key, value) => {
  try {
    if (value) localStorage.setItem(key, JSON.stringify(value));
    else localStorage.removeItem(key);
  } catch {
    throw Error(
      "No se pudo guardar la operación pendiente en este dispositivo. Reintenta antes de continuar.",
    );
  }
};
const AmountInput = ({ value, onChange, disabled, label, children }) => {
  const press = (digit) => {
    if (digit === "C") return onChange("0,00");
    const parsed = readCashAmount(value);
    let digits = String(
      Math.round((Number.isFinite(parsed) ? parsed : 0) * 100),
    );
    digits = digit === "back" ? digits.slice(0, -1) : digits + digit;
    if (digits.length > 9) return;
    onChange((Number(digits || 0) / 100).toFixed(2).replace(".", ","));
  };
  return (
    <>
      <div className="cashInputFields">
        <label className="cashAmountLabel">
          {label}
          <input
            inputMode="decimal"
            autoComplete="off"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            aria-label={label}
          />
        </label>
        {children}
      </div>
      <div className="cashKeypad" aria-label="Teclado de importe">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "back"].map(
          (key) => (
            <button
              type="button"
              key={key}
              disabled={disabled}
              aria-label={
                key === "back"
                  ? "Borrar última cifra"
                  : key === "C"
                    ? "Limpiar importe"
                    : key
              }
              onClick={() => press(key)}
            >
              {key === "back" ? "⌫" : key}
            </button>
          ),
        )}
      </div>
    </>
  );
};
const Rows = ({ items }) => (
  <dl className="cashRows">
    {items.map(([label, value]) => (
      <div key={label}>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    ))}
  </dl>
);
const Summary = ({ summary }) => (
  <Rows
    items={[
      ["Ventas totales", euros(summary.salesCents)],
      ["Efectivo", euros(summary.cashCents)],
      ["Tarjeta", euros(summary.cardCents)],
      ["Otros métodos", euros(summary.otherCents)],
    ]}
  />
);
const Panel = ({ title, children }) => (
  <section className="cashPanel">
    <h2>{title}</h2>
    <div className="cashPanelBody">{children}</div>
  </section>
);
const Difference = ({ value }) => (
  <span className={`cashBadge ${value === 0 ? "cashGood" : "cashDifference"}`}>
    {value === 0
      ? "CUADRA"
      : `${value < 0 ? "FALTA" : "SOBRA"} ${euros(Math.abs(value))}`}
  </span>
);

export const Cash = ({ expectedPointId, expectedOperatorId, openOnLoad = false }) => {
  const openingIntent = useRef(openOnLoad);
  const [data, setData] = useState(null),
    [history, setHistory] = useState(null),
    [page, setPage] = useState(1),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [modal, setModal] = useState(null),
    [detail, setDetail] = useState(null),
    [amount, setAmount] = useState("0,00"),
    [type, setType] = useState("IN"),
    [reason, setReason] = useState(""),
    [notes, setNotes] = useState(""),
    [kinds, setKinds] = useState({});
  const [pending, setPending] = useState(null),
    [busy, setBusy] = useState(false),
    [operationError, setOperationError] = useState("");
  const inFlight = useRef(false),
    readVersion = useRef(0);
  const load = useCallback(async () => {
    const version = ++readVersion.current;
    setLoading(true);
    setError("");
    try {
      const [state, closed] = await Promise.all([
        getCashState(),
        getCashHistory(page),
      ]);
      if (version !== readVersion.current) return;
      if (
        state?.error !== false ||
        closed?.error !== false ||
        !state.data?.pointOfSaleId ||
        !Array.isArray(state.data.methods) ||
        !Array.isArray(closed.data?.rows)
      )
        throw Error(
          state?.message || closed?.message || "No se pudo cargar la caja.",
        );
      if (
        (expectedPointId && state.data.pointOfSaleId !== expectedPointId) ||
        (expectedOperatorId && state.data.operatorId !== expectedOperatorId)
      )
        throw Error(
          "El puesto o el operador cambió en otra pestaña. Vuelve a seleccionar tu puesto antes de continuar.",
        );
      setData(state.data);
      setHistory(closed.data);
      setKinds(
        Object.fromEntries(
          state.data.methods.map((method) => [
            method.paymentMethodId,
            method.kind || "",
          ]),
        ),
      );
      const journal = readJournal(cashJournalKey(state.data));
      if (openingIntent.current) {
        openingIntent.current = false;
        if (!journal && !state.data.current) setModal("open");
      }
      if (journal) {
        setPending(journal);
        setModal("pending");
        setOperationError(
          "Hay una operación pendiente de confirmar. Reintenta la misma operación para recuperar su resultado.",
        );
      }
    } catch (e) {
      if (version === readVersion.current)
        setError(e.message || "No se pudo cargar la caja.");
    } finally {
      if (version === readVersion.current) setLoading(false);
    }
  }, [page, expectedPointId, expectedOperatorId]);
  useEffect(() => {
    load();
    return () => {
      readVersion.current += 1;
    };
  }, [load]);
  const start = (kind, movementType) => {
    setAmount(kind === "close" ? "" : "0,00");
    setNotes("");
    setType(movementType || "IN");
    setReason("");
    setOperationError("");
    setDetail(null);
    setModal(kind);
  };
  const submit = async (operation) => {
    if (inFlight.current || !data) return;
    inFlight.current = true;
    setBusy(true);
    setOperationError("");
    const journalKey = cashJournalKey(data);
    try {
      // Persist the exact key/body before sending. A lost response must never create a second movement.
      writeJournal(journalKey, operation);
      setPending(operation);
      const response = await submitCashOperation(operation);
      if (
        response?.error === false &&
        !response.data?.shiftId &&
        !response.data?.movementId
      ) {
        setModal("pending");
        throw Error(
          "Respuesta no válida. Recupera la misma operación sin duplicarla.",
        );
      }
      if (response?.error !== false) {
        if (response?.data?.cashOperationRejected === true) {
          writeJournal(journalKey, null);
          setPending(null);
          if (modal === "pending") setModal(null);
        } else {
          setModal("pending");
        }
        throw Error(
          response?.message ||
            "No se ha confirmado la operación. Reintenta sin duplicarla.",
        );
      }
      writeJournal(journalKey, null);
      setPending(null);
      setModal(null);
      await load();
    } catch (e) {
      if (readJournal(journalKey)) setModal("pending");
      setOperationError(e.message || "No se ha confirmado la operación.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const send = () => {
    const value = readCashAmount(amount);
    if (!Number.isFinite(value)) return;
    const requestId = crypto.randomUUID();
    const expected = {
      expectedPointOfSaleId: data.pointOfSaleId,
      expectedOperatorId: data.operatorId,
    };
    if (modal === "open")
      return submit({
        path: "cash-register/open",
        body: {
          requestId,
          ...expected,
          openingFund: value,
          methodKinds: kinds,
        },
      });
    if (modal === "close")
      return submit({
        path: `cash-register/shifts/${data.current.shiftId}/close`,
        body: { requestId, ...expected, countedCash: value },
      });
    return submit({
      path: `cash-register/shifts/${data.current.shiftId}/movements`,
      body: { requestId, ...expected, type, amount: value, reason, notes },
    });
  };
  const showClose = async (id) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setOperationError("");
    try {
      const response = await getCashClose(id);
      if (response?.error !== false || !response.data?.summary)
        throw Error(response?.message || "No se pudo cargar el cierre.");
      setDetail(response.data);
      setModal("detail");
    } catch (e) {
      setError(e.message);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const current = data?.current,
    summary = current?.summary;
  const value = readCashAmount(amount);
  const valid =
    Number.isFinite(value) &&
    (modal !== "movement" || (value > 0 && reason)) &&
    (modal !== "open" ||
      (data?.methods.length > 0 &&
        data.methods.every((method) =>
          ["cash", "card", "other"].includes(kinds[method.paymentMethodId]),
        )));
  return (
    <main className="cashPage">
      <div className="cashTitle">
        <h1>Caja</h1>
        <span>{history ? `${history.count} ${history.count === 1 ? "cierre registrado" : "cierres registrados"}` : ""}</span>
        <small>{data?.pointName}</small>
        <button
          type="button"
          disabled={loading || busy || Boolean(pending)}
          onClick={load}
        >
          Actualizar
        </button>
      </div>
      {loading && <p role="status">Cargando caja…</p>}
      {error && (
        <p role="alert">
          {error}{" "}
          <button type="button" disabled={loading} onClick={load}>
            Reintentar
          </button>
        </p>
      )}
      {pending && modal !== "pending" && (
        <p role="status" className="cashOperationError">
          Hay una operación pendiente de confirmar.{" "}
          <button
            type="button"
            disabled={busy}
            onClick={() => setModal("pending")}
          >
            REVISAR OPERACIÓN PENDIENTE
          </button>
        </p>
      )}
      {!loading && !error && data && (
        <div className="cashColumns">
          <div>
            <Panel title="Estado de caja">
              {current && <div className="cashStatus">
                <span className="cashBadge cashGood">ABIERTA</span>
                <strong>Turno abierto desde {date(current.openedAt)}</strong>
              </div>}
              {current ? (
                <>
                  <div className="cashOpening">
                    <div>
                      <small>EMPLEADO QUE ABRIÓ</small>
                      <strong>{current.openedByName}</strong>
                    </div>
                    <div>
                      <small>HORA DE APERTURA</small>
                      <strong>{date(current.openedAt)}</strong>
                    </div>
                    <div>
                      <small>FONDO INICIAL</small>
                      <strong>{euros(current.openingFundCents)}</strong>
                    </div>
                  </div>
                  <div className="cashActions">
                    <button
                      type="button"
                      disabled={busy || Boolean(pending)}
                      onClick={() => start("movement", "IN")}
                    >
                      <LuArrowDownToLine /> ENTRADA
                    </button>
                    <button
                      type="button"
                      disabled={busy || Boolean(pending)}
                      onClick={() => start("movement", "OUT")}
                    >
                      <LuArrowUpFromLine /> SALIDA
                    </button>
                    <button
                      type="button"
                      className="cashPrimary"
                      disabled={busy || Boolean(pending)}
                      onClick={() => start("close")}
                    >
                      <LuLock /> CERRAR CAJA
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="cashClosedIntro">
                    <span className="cashClosedIcon" aria-hidden="true"><LuWallet /></span>
                    <div>
                      <div className="cashStatus"><strong>Caja cerrada</strong><span className="cashBadge">CERRADA</span></div>
                      <p>
                        {data.enabled
                          ? "Abre un turno para registrar los cobros de este TPV."
                          : "La primera apertura activará el control de caja de este puesto. Los cobros posteriores necesitarán un turno abierto; las ventas anteriores no se añaden al historial."}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="cashPrimary cashOpenButton"
                    disabled={busy || Boolean(pending)}
                    onClick={() => start("open")}
                  >
                    <LuWallet /> ABRIR CAJA
                  </button>
                </>
              )}
            </Panel>
            <Panel title="Historial de cierres">
              {history.rows.length ? (
                history.rows.map((row) => (
                  <button
                    type="button"
                    className="cashHistoryRow"
                    key={row.shiftId}
                    disabled={busy}
                    onClick={() => showClose(row.shiftId)}
                  >
                    <strong>{date(row.closedAt)}</strong>
                    <div>
                      <b>{row.closedByName}</b>
                      <small>
                        Apertura {date(row.openedAt)} · Cierre{" "}
                        {date(row.closedAt)}
                      </small>
                    </div>
                    <div>
                      <small>VENTAS</small>
                      <b>{euros(row.closeSnapshot.salesCents)}</b>
                    </div>
                    <Difference value={row.closeSnapshot.differenceCents} />
                    <LuChevronRight />
                  </button>
                ))
              ) : (
                <p>Todavía no hay cierres registrados en este TPV.</p>
              )}
              {history.count > 20 && (
                <nav className="cashPagination" aria-label="Páginas de cierres">
                  <button
                    type="button"
                    disabled={page === 1 || busy}
                    onClick={() => setPage(page - 1)}
                  >
                    Anterior
                  </button>
                  <span>
                    {page} / {Math.ceil(history.count / 20)}
                  </span>
                  <button
                    type="button"
                    disabled={page * 20 >= history.count || busy}
                    onClick={() => setPage(page + 1)}
                  >
                    Siguiente
                  </button>
                </nav>
              )}
            </Panel>
          </div>
          <aside>
            {!current ? <Panel title="Resumen del turno">
              <div className="cashClosedSummary">
                <LuLock aria-hidden="true" />
                <strong>Sin turno abierto</strong>
                <p>Las ventas y el efectivo del nuevo turno aparecerán aquí cuando abras la caja.</p>
              </div>
            </Panel> : <>
            <Panel title="Resumen del turno">
              {summary ? (
                <Summary summary={summary} />
              ) : (
                <p>El resumen estará disponible al abrir un turno.</p>
              )}
            </Panel>
            <Panel title="Efectivo del turno">
              {summary ? (
                <>
                  <Rows
                    items={[
                      [
                        "Entradas de efectivo",
                        `+${euros(summary.entriesCents)}`,
                      ],
                      [
                        "Salidas de efectivo",
                        `−${euros(summary.withdrawalsCents)}`,
                      ],
                    ]}
                  />
                  <div className="cashExpected">
                    <span>Efectivo esperado</span>
                    <strong>{euros(summary.expectedCashCents)}</strong>
                  </div>
                  <p className="cashFormula">
                    Fondo inicial + cobros en efectivo, netos de cambio +
                    entradas − salidas.
                  </p>
                </>
              ) : (
                <p>Sin turno abierto.</p>
              )}
            </Panel>
            </>}
          </aside>
        </div>
      )}
      {modal && (
        <TpvDialog
          title={
            modal === "pending"
              ? "Operación pendiente de confirmar"
              : modal === "open"
                ? "Abrir caja"
                : modal === "close"
                  ? "Cerrar caja"
                  : modal === "detail"
                    ? `Cierre del ${date(detail.closedAt)}`
                    : "Movimiento de caja"
          }
          className="cashDialog"
          busy={busy}
          onClose={() => {
            if (!busy) setModal(null);
          }}
        >
          <div className="tpvDialogBody cashDialogBody">
            {operationError && (
              <p role="alert" className="cashOperationError">
                {operationError}
              </p>
            )}
            {modal === "pending" ? (
              <>
                <p>
                  La respuesta pudo perderse. Se enviará el mismo identificador
                  y los mismos datos; no se creará otra operación.
                </p>
                <button
                  type="button"
                  disabled={busy || !pending}
                  className="cashPrimary cashFull"
                  onClick={() => submit(pending)}
                >
                  {busy ? "Confirmando…" : "REINTENTAR OPERACIÓN"}
                </button>
              </>
            ) : modal === "detail" ? (
              <>
                <h3>CUADRE DE EFECTIVO</h3>
                <Rows
                  items={[
                    [
                      "Efectivo esperado",
                      euros(detail.summary.expectedCashCents),
                    ],
                    [
                      "Efectivo contado",
                      euros(detail.summary.countedCashCents),
                    ],
                    [
                      "Diferencia",
                      <Difference key="difference" value={detail.summary.differenceCents} />,
                    ],
                  ]}
                />
                <h3>VENTAS DEL TURNO</h3>
                <Summary summary={detail.summary} />
                <h3>RESPONSABLES</h3>
                <Rows
                  items={[
                    [
                      "Empleado que abrió",
                      `${detail.openedByName} · ${date(detail.openedAt)}`,
                    ],
                    [
                      "Empleado que cerró",
                      `${detail.closedByName} · ${date(detail.closedAt)}`,
                    ],
                  ]}
                />
                <h3>MOVIMIENTOS</h3>
                {detail.summary.movements.length ? (
                  <Rows
                    items={detail.summary.movements.map((movement, index) => [
                      `${index + 1}. ${movement.type === "IN" ? "Entrada" : "Salida"} · ${date(movement.createdAt)} · ${movement.userName}`,
                      <span key={movement.movementId || index}>
                        {euros(movement.amountCents)} ·{" "}
                        {(reasons[movement.type].find(
                          (item) => item[0] === movement.reason,
                        ) || [])[1] || movement.reason}
                        {movement.notes ? ` · ${movement.notes}` : ""}
                      </span>,
                    ])}
                  />
                ) : (
                  <p>Sin movimientos manuales.</p>
                )}
                <button
                  type="button"
                  className="cashFull"
                  onClick={() => setModal(null)}
                >
                  VOLVER AL HISTORIAL
                </button>
              </>
            ) : (
              <>
                {modal === "movement" && (
                  <div className="cashType">
                    <button
                      type="button"
                      aria-pressed={type === "IN"}
                      disabled={busy}
                      onClick={() => {
                        setType("IN");
                        setReason("");
                      }}
                    >
                      <LuArrowDownToLine /> ENTRADA
                    </button>
                    <button
                      type="button"
                      aria-pressed={type === "OUT"}
                      disabled={busy}
                      onClick={() => {
                        setType("OUT");
                        setReason("");
                      }}
                    >
                      <LuArrowUpFromLine /> SALIDA
                    </button>
                  </div>
                )}
                {modal === "close" && (
                  <>
                    <p>
                      Cuenta el efectivo físico de esta caja. El cierre guarda
                      el cuadre definitivo y no puede editarse.
                    </p>
                    <Rows
                      items={[
                        [
                          "Efectivo esperado",
                          euros(summary?.expectedCashCents),
                        ],
                      ]}
                    />
                  </>
                )}
                <div className="cashInputGrid">
                  <AmountInput
                    value={amount}
                    onChange={setAmount}
                    disabled={busy}
                    label={
                      modal === "open"
                        ? "Fondo inicial"
                        : modal === "close"
                          ? "Efectivo contado"
                          : "Importe"
                    }
                  >
                    {" "}
                    {modal === "movement" && (
                      <>
                        <h3>MOTIVO</h3>
                        <div className="cashReasons">
                          {reasons[type].map(([id, label]) => (
                            <button
                              type="button"
                              key={id}
                              disabled={busy}
                              aria-pressed={reason === id}
                              onClick={() => setReason(id)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <label className="cashNotes">
                          NOTAS (OPCIONAL)
                          <textarea
                            maxLength={1000}
                            value={notes}
                            disabled={busy}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Añade una observación"
                          />
                        </label>
                      </>
                    )}
                  </AmountInput>
                </div>
                {modal === "open" && (
                  <div className="cashMethodKinds">
                    <h3>CLASIFICACIÓN DE LOS MÉTODOS DE PAGO</h3>
                    <p>
                      Indica cuáles mueven efectivo físico de esta caja. Se
                      guarda para el turno completo.
                    </p>
                    {data.methods.map((method) => (
                      <label key={method.paymentMethodId}>
                        {method.name}
                        <select
                          aria-label={`Tipo de ${method.name}`}
                          value={kinds[method.paymentMethodId] || ""}
                          disabled={busy}
                          onChange={(e) =>
                            setKinds({
                              ...kinds,
                              [method.paymentMethodId]: e.target.value,
                            })
                          }
                        >
                          <option value="">Seleccionar tipo</option>
                          <option value="cash">Efectivo</option>
                          <option value="card">Tarjeta</option>
                          <option value="other">Otro método</option>
                        </select>
                      </label>
                    ))}
                    {!data.methods.length && (
                      <p role="alert">
                        No hay métodos de pago activos en este negocio.
                      </p>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="cashPrimary cashFull"
                  disabled={!valid || busy}
                  onClick={send}
                >
                  {busy
                    ? "GUARDANDO…"
                    : modal === "open"
                      ? "ABRIR CAJA"
                      : modal === "close"
                        ? "CONFIRMAR CIERRE"
                        : "REGISTRAR MOVIMIENTO"}
                </button>
              </>
            )}
          </div>
        </TpvDialog>
      )}
    </main>
  );
};
